<script setup>
// NOTICE: KAPTION-INTEGRATION — Horizontal conversion-rate bars for campaign reports.
// Four rates: delivery, read, reply, failure. Values are PRE-AGGREGATED by the
// wanalytics API (Kaption::AnalyticsDataService) — passed straight through as props.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  // { delivery_rate, read_rate, reply_rate, failure_rate } — percentages 0..100
  rates: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();

// Colour variants mirror the sibling KaptionMetricCard (slate/teal/green/ruby).
const RATE_ROWS = computed(() => [
  {
    key: 'delivery_rate',
    label: t('CAMPAIGN.REPORT.RATES.DELIVERY_RATE'),
    value: props.rates.delivery_rate ?? 0,
    bar: 'bg-n-teal-9',
    text: 'text-n-teal-11',
    track: 'bg-n-teal-4',
  },
  {
    key: 'read_rate',
    label: t('CAMPAIGN.REPORT.RATES.READ_RATE'),
    value: props.rates.read_rate ?? 0,
    bar: 'bg-n-teal-9',
    text: 'text-n-teal-11',
    track: 'bg-n-teal-4',
  },
  {
    key: 'reply_rate',
    label: t('CAMPAIGN.REPORT.RATES.REPLY_RATE'),
    value: props.rates.reply_rate ?? 0,
    bar: 'bg-n-green-9',
    text: 'text-n-green-11',
    track: 'bg-n-green-4',
  },
  // NOTICE: KAPTION-INTEGRATION — click-through rate (clicks ÷ delivered)
  {
    key: 'click_through_rate',
    label: t('CAMPAIGN.REPORT.RATES.CLICK_THROUGH_RATE'),
    value: props.rates.click_through_rate ?? 0,
    bar: 'bg-n-green-9',
    text: 'text-n-green-11',
    track: 'bg-n-green-4',
  },
  {
    key: 'failure_rate',
    label: t('CAMPAIGN.REPORT.RATES.FAILURE_RATE'),
    value: props.rates.failure_rate ?? 0,
    bar: 'bg-n-ruby-9',
    text: 'text-n-ruby-11',
    track: 'bg-n-ruby-4',
  },
]);

const clamp = v => Math.min(Math.max(Number(v) || 0, 0), 100);
const formatPct = v => `${(Number(v) || 0).toFixed(1)}%`;
</script>

<template>
  <div class="flex flex-col gap-3">
    <h3 class="text-sm font-semibold text-n-slate-12">
      {{ t('CAMPAIGN.REPORT.RATES.TITLE') }}
    </h3>

    <div
      class="flex flex-col gap-4 rounded-xl border border-n-weak bg-n-alpha-black2 p-4"
    >
      <div
        v-for="row in RATE_ROWS"
        :key="row.key"
        class="flex flex-col gap-1.5"
      >
        <div class="flex items-center justify-between">
          <span class="text-sm font-medium text-n-slate-11">
            {{ row.label }}
          </span>
          <span class="text-sm font-bold tabular-nums" :class="row.text">
            {{ formatPct(row.value) }}
          </span>
        </div>
        <div class="w-full h-2 rounded-full overflow-hidden" :class="row.track">
          <div
            class="h-full rounded-full transition-all duration-500"
            :class="row.bar"
            :style="{ width: `${clamp(row.value)}%` }"
          />
        </div>
      </div>
    </div>
  </div>
</template>
